import { useState, useCallback } from "react";
import { ScanLine, CheckCircle2, Wrench, CloudOff, Printer } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { beep, isNetworkError } from "@/lib/scanFx";
import { useOfflineQueue } from "@/lib/useOfflineQueue";
import { useApp } from "@/context/AppContext";
import { PageHeader, DataTable, StatusBadge, PctChip, Loading } from "@/components/bits";
import { CameraScanner } from "@/components/CameraScanner";
import { OfflineBanner } from "@/components/OfflineBanner";
import { BulkQueue, OperatorStats } from "@/components/qcBits";
import { PrintOverlay } from "@/components/printDocs";
import { RepairPrintView } from "@/components/RepairPrintView";
import { HistoryActions, EditScanDialog, DeleteHistoryDialog, useHistory, HistoryControls, LoadMore } from "@/components/scanHistoryBits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

const sendQc = (it) => api.post("/qc/scan", { code: it.code, result: it.result, note: it.note || "" });
const today = () => new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);

export default function QC() {
  const { user } = useApp();
  const [code, setCode] = useState("");
  const [mark, setMark] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [bulk, setBulk] = useState(false);
  const [queue, setQueue] = useState([]);
  const [tick, setTick] = useState(0);
  const [repairDoc, setRepairDoc] = useState(null);
  const [editScan, setEditScan] = useState(null);
  const [delScan, setDelScan] = useState(null);

  const openRepairPrint = async () => {
    try {
      const { data } = await api.get(`/scans/repair?date=${hist.date || today()}`);
      setRepairDoc(data);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const bump = useCallback(() => setTick((t) => t + 1), []);
  const hist = useHistory("/scans", bump);
  const { rows: riwayat, refresh } = hist;
  const offline = useOfflineQueue("hl_qc_offline", sendQc, refresh);

  const lookup = useCallback(async (c) => {
    const kode = c.trim().toUpperCase();
    if (!kode) return;
    try {
      const { data } = await api.get(`/markings?code=${encodeURIComponent(kode)}&limit=1`);
      if (!data.length) { setMark(null); beep("error"); toast.error(`Marking ${kode} tidak ditemukan`); return; }
      const m = data[0];
      if (bulk) {
        setQueue((q) => {
          if (q.some((x) => x.code === m.code)) { toast.warning(`${m.code} sudah ada di antrean`); return q; }
          if (m.qcOk >= m.qty) { beep("error"); toast.error(`${m.code} sudah lolos QC lengkap (${m.qty}/${m.qty})`); return q; }
          return [...q, { code: m.code, projectName: m.projectName, qcOk: m.qcOk, qty: m.qty }];
        });
        setCode("");
      } else {
        setMark(m);
      }
    } catch (e) {
      if (isNetworkError(e)) {
        // Offline: tetap izinkan input hasil, validasi dilakukan server saat sinkron
        if (bulk) setQueue((q) => (q.some((x) => x.code === kode) ? q : [...q, { code: kode, projectName: "(offline)", qcOk: "?", qty: "?" }]));
        else setMark({ code: kode, offline: true, projectName: "Offline — data marking belum terverifikasi", qty: "?", qcOk: "?", stb: "?", delivery: "?", progress: 0 });
        setCode("");
        return;
      }
      toast.error(fmtErr(e));
    }
  }, [bulk]);

  const onCameraScan = useCallback((text) => { setCode(text); lookup(text); }, [lookup]);

  const scan = async (result) => {
    if (!mark) { toast.error("Cari marking terlebih dahulu"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/qc/scan", { code: mark.code, result, note });
      beep("ok");
      toast.success(`${mark.code} → ${result === "OK" ? "QC OK ✓" : "Repair"}`);
      setMark({ ...data.marking });
      refresh();
    } catch (e) {
      if (isNetworkError(e)) {
        offline.add({ code: mark.code, result, note });
        toast.warning(`${mark.code} disimpan offline, akan disinkron saat online`);
        setMark(null); setCode(""); setNote("");
      } else { beep("error"); toast.error(fmtErr(e)); }
    }
    setBusy(false);
  };

  const submitBulk = async () => {
    const items = queue.filter((i) => !i.status || i.status === "error").map((i) => ({ code: i.code, result: "OK", note: "" }));
    if (!items.length) return;
    setBusy(true);
    try {
      const { data } = await api.post("/qc/scan/bulk", { items });
      const rmap = Object.fromEntries(data.results.map((r) => [r.code, r]));
      setQueue((q) => q.map((i) => (rmap[i.code] ? { ...i, status: rmap[i.code].ok ? "ok" : "error", detail: rmap[i.code].detail, qcOk: rmap[i.code].qcOk ?? i.qcOk } : i)));
      beep(data.failed ? "error" : "ok");
      toast[data.failed ? "warning" : "success"](`Scan massal: ${data.success} OK${data.failed ? ` · ${data.failed} ditolak` : ""}`);
      refresh();
    } catch (e) {
      if (isNetworkError(e)) {
        items.forEach((it) => offline.add(it));
        setQueue([]);
        toast.warning(`${items.length} scan disimpan offline, akan disinkron saat online`);
      } else { beep("error"); toast.error(fmtErr(e)); }
    }
    setBusy(false);
  };

  return (
    <div className="space-y-5">
      <PageHeader id="QC Inspection Console" zh="质量检验扫描" desc="Scan marking → verifikasi kualitas → OK atau Repair." />

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="panel scanlines relative overflow-hidden p-5" data-testid="qc-scanner-panel">
          <div className="pointer-events-none absolute inset-x-0 top-0 h-0.5 overflow-hidden">
            <div className="sweep h-full w-1/3 bg-gradient-to-r from-transparent via-emerald-400 to-transparent" />
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400">
            <ScanLine className="h-4 w-4 text-emerald-400" /> Scan label QR dengan kamera, scanner USB, atau ketik kode marking
            <label className="ml-auto inline-flex cursor-pointer items-center gap-2 text-slate-300" data-testid="qc-bulk-toggle-label">
              <Switch checked={bulk} onCheckedChange={(v) => { setBulk(v); setMark(null); }} data-testid="qc-bulk-toggle" />
              Mode Massal <span className="cn text-slate-500">批量</span>
            </label>
          </div>
          <form className="mt-3 flex gap-2" onSubmit={(e) => { e.preventDefault(); lookup(code); }}>
            <Input autoFocus value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="B01-001…"
              className="border-slate-800 bg-slate-900/70 font-mono" data-testid="qc-scan-input" />
            <Button type="submit" variant="outline" className="border-slate-700" data-testid="qc-lookup-button">Cari</Button>
          </form>
          <div className="mt-3"><CameraScanner onScan={onCameraScan} accent="emerald" testid="qc-camera" /></div>
          <OfflineBanner queue={offline} testid="qc-offline" accent="emerald" />

          {bulk ? (
            <BulkQueue items={queue} busy={busy} onRemove={(c) => setQueue((q) => q.filter((x) => x.code !== c))} onClear={() => setQueue([])} onSubmit={submitBulk} />
          ) : mark ? (
            <div className="mt-4 rounded border border-slate-800 bg-[#0B1322] p-4" data-testid="qc-marking-card">
              <div className="flex items-center justify-between">
                <div className="font-mono text-xl font-bold text-amber-400">{mark.code}</div>
                {mark.offline ? <span className="inline-flex items-center gap-1 rounded border border-rose-500/40 px-2 py-0.5 text-[11px] text-rose-300" data-testid="qc-offline-mark"><CloudOff className="h-3 w-3" />Offline</span> : <PctChip value={mark.progress} />}
              </div>
              <div className="mt-1 text-sm text-slate-300">{mark.projectName}{mark.vendorName ? ` · ${mark.vendorName} · Area ${mark.area}` : ""}</div>
              <div className="mt-3 grid grid-cols-4 gap-2 text-center">
                {[["Qty", mark.qty, "text-slate-100"], ["QC OK", mark.qcOk, "text-emerald-400"], ["STB", mark.stb, "text-indigo-300"], ["Delivery", mark.delivery, "text-amber-300"]].map(([l, v, c]) => (
                  <div key={l} className="rounded bg-slate-900/70 p-2"><div className="text-[10px] text-slate-500">{l}</div><div className={cn("font-mono text-lg", c)}>{v}</div></div>
                ))}
              </div>
              <div className="mt-4 flex gap-2">
                <Button disabled={busy} onClick={() => scan("OK")} className="flex-1 bg-emerald-600 hover:bg-emerald-500" data-testid="qc-ok-button">
                  <CheckCircle2 className="mr-2 h-4 w-4" />OK / 合格
                </Button>
                <Button disabled={busy} onClick={() => scan("Repair")} variant="outline" className="flex-1 border-amber-500/50 text-amber-400 hover:bg-amber-500/10" data-testid="qc-repair-button">
                  <Wrench className="mr-2 h-4 w-4" />Repair / 修复
                </Button>
              </div>
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Catatan defect (opsional, untuk Repair)"
                className="mt-2 border-slate-800 bg-slate-900/70 text-xs" data-testid="qc-note-input" />
            </div>
          ) : (
            <div className="mt-4 rounded border border-dashed border-slate-800 p-8 text-center text-sm text-slate-600" data-testid="qc-empty-state">
              Belum ada marking dipindai<br /><span className="cn text-xs">请扫描构件条码</span>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <OperatorStats refreshKey={tick} />
          <div className="panel p-4" data-testid="qc-riwayat-panel">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <h3 className="font-display text-sm font-bold text-slate-200">Riwayat Scan <span className="cn text-[11px] font-normal text-slate-500">扫描记录</span></h3>
              <div className="ml-auto flex flex-wrap items-center gap-2" data-testid="qc-repair-print-controls">
                <HistoryControls h={hist} testid="qc-riwayat" kind="qc" path="/scans" user={user} />
                <Button type="button" size="sm" variant="outline" onClick={openRepairPrint} className="h-8 border-amber-500/40 text-xs text-amber-300 hover:bg-amber-500/10" data-testid="qc-repair-print-button">
                  <Printer className="mr-1.5 h-3.5 w-3.5" /> Cetak Daftar Repair (A4)
                </Button>
              </div>
            </div>
            {!riwayat ? <Loading /> : (
              <DataTable testid="qc-riwayat-table" rows={riwayat} columns={[
                { key: "at", label: "Waktu", render: (r) => <span className="font-mono text-xs text-slate-400">{new Date(r.at).toLocaleString("id-ID", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span> },
                { key: "code", label: "Marking", render: (r) => <span className="font-mono text-amber-400">{r.code}</span> },
                { key: "projectName", label: "Proyek", render: (r) => <span className="text-slate-400 text-xs">{r.projectName}</span> },
                { key: "qty", label: "Qty", render: (r) => <span className="font-mono">{r.qty} pcs</span> },
                { key: "result", label: "Hasil", render: (r) => <StatusBadge status={r.result} /> },
                { key: "by", label: "Oleh", render: (r) => <span className="text-slate-400 text-xs">{r.by}{r.editedAt && <span className="ml-1 text-[10px] text-sky-500" title={`Diedit ${r.editedBy}`}>(edit)</span>}</span> },
                { key: "aksi", label: "Aksi", render: (r) => <HistoryActions row={r} user={user} onEdit={setEditScan} onDelete={setDelScan} testid={`qc-riwayat-${r.id}`} /> },
              ]} />
            )}
            <LoadMore h={hist} testid="qc-riwayat" />
          </div>
        </div>
      </div>

      {editScan && <EditScanDialog scan={editScan} onClose={() => setEditScan(null)} onSaved={refresh} />}
      {delScan && <DeleteHistoryDialog row={delScan} path="/scans" label="Scan QC" onClose={() => setDelScan(null)} onDeleted={refresh} />}

      {repairDoc && (
        <PrintOverlay testid="repair-print-overlay" onClose={() => setRepairDoc(null)}>
          <RepairPrintView data={repairDoc} user={user} />
        </PrintOverlay>
      )}
    </div>
  );
}
